import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { DataTable } from "../../components/DataTable";
import { EmptyState, TableSkeleton } from "../../components/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { api, apiError } from "../../lib/api";
import { formatDate, formatINR } from "../../lib/format";

const TYPES = ["Deposit", "Bonus", "Reinvestment", "Referral", "Adjustment"];

export default function Investments() {
  const [state, setState] = useState({ items: [], total: 0, pages: 1, totalAmount: 0 });
  const [page, setPage] = useState(1);
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/client/investments", {
        params: { page, limit: 10, ...(type !== "all" ? { type } : {}) },
      });
      setState(data);
    } catch (error) {
      toast.error(apiError(error, "Could not load investment history"));
    } finally {
      setLoading(false);
    }
  }, [page, type]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => formatDate(row.original.date),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <span className="inline-flex items-center rounded-full border border-border surface-2 px-2.5 py-0.5 text-xs font-medium">
            {row.original.type}
          </span>
        ),
      },
      {
        accessorKey: "note",
        header: "Note",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.note || "\u2014"}</span>
        ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        meta: { numeric: true },
        cell: ({ row }) => formatINR(row.original.amount),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Investment history"
        subtitle={`${state.total} entr${state.total === 1 ? "y" : "ies"} · ${formatINR(
          state.totalAmount
        )} recorded`}
        actions={
          <Select
            value={type}
            onValueChange={(v) => {
              setType(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[180px]" data-testid="investments-type-filter">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {loading ? (
        <TableSkeleton cols={4} />
      ) : state.items.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title="No investment entries"
          description={
            type === "all"
              ? "No records yet. New entries will appear here once added by your manager."
              : `No ${type} entries found. Try a different filter.`
          }
          testId="investments-empty-state"
        />
      ) : (
        <DataTable
          columns={columns}
          data={state.items}
          page={page}
          pages={state.pages}
          total={state.total}
          onPageChange={setPage}
          testId="investments-table"
          rowTestId="investment-row"
        />
      )}
    </div>
  );
}
